import React, { useState } from 'react';
import { Eye, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { format } from 'date-fns';

export default function RefinementSuggestionsPanel({ refinements, filters, onFiltersChange, onUpdateStatus, onDelete, onRefresh }) {
  const [selectedRefinement, setSelectedRefinement] = useState(null);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'implemented': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      'faq_add': 'Ajout FAQ',
      'faq_update': 'Mise à jour FAQ',
      'faq_delete': 'Suppression FAQ',
      'knowledge_update': 'Mise à jour Connaissances',
      'product_update': 'Mise à jour Produit',
      'rule_update': 'Mise à jour Règle'
    };
    return labels?.[type] || type;
  };

  const handleApprove = async (refinementId) => {
    const notes = prompt('Notes d\'approbation (optionnel):');
    await onUpdateStatus(refinementId, 'approved', notes || 'Approuvé');
  };

  const handleReject = async (refinementId) => {
    const notes = prompt('Raison du rejet:');
    if (notes) {
      await onUpdateStatus(refinementId, 'rejected', notes);
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex gap-2 flex-wrap">
          <select
            value={filters?.status || 'all'}
            onChange={(e) => onFiltersChange({ ...filters, status: e?.target?.value === 'all' ? null : e?.target?.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="all">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="approved">Approuvés</option>
            <option value="rejected">Rejetés</option>
            <option value="implemented">Implémentés</option>
          </select>

          <select
            value={filters?.priority || 'all'}
            onChange={(e) => onFiltersChange({ ...filters, priority: e?.target?.value === 'all' ? null : e?.target?.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="all">Toutes priorités</option>
            <option value="high">Haute</option>
            <option value="medium">Moyenne</option>
            <option value="low">Basse</option>
          </select>

          <select
            value={filters?.refinementType || 'all'}
            onChange={(e) => onFiltersChange({ ...filters, refinementType: e?.target?.value === 'all' ? null : e?.target?.value })}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="all">Tous les types</option>
            <option value="faq_add">Ajout FAQ</option>
            <option value="faq_update">Mise à jour FAQ</option>
            <option value="knowledge_update">Connaissances</option>
            <option value="product_update">Produits</option>
            <option value="rule_update">Règles</option>
          </select>
        </div>

        <button
          onClick={onRefresh}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
        >
          Actualiser
        </button>
      </div>
      {/* Refinements List */}
      <div className="space-y-4">
        {refinements?.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <p className="text-gray-600">Aucune suggestion trouvée</p>
          </div>
        ) : (
          refinements?.map((refinement) => (
            <div key={refinement?.id} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getPriorityColor(refinement?.priority)}`}>
                      {refinement?.priority === 'high' && '🔴 Haute'}
                      {refinement?.priority === 'medium' && '🟡 Moyenne'}
                      {refinement?.priority === 'low' && '🔵 Basse'}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(refinement?.status)}`}>
                      {refinement?.status === 'pending' && 'En attente'}
                      {refinement?.status === 'approved' && 'Approuvé'}
                      {refinement?.status === 'rejected' && 'Rejeté'}
                      {refinement?.status === 'implemented' && 'Implémenté'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {getTypeLabel(refinement?.refinement_type)}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{refinement?.title}</h3>
                  {refinement?.category && (
                    <p className="text-sm text-gray-600 mb-2">Catégorie: {refinement?.category}</p>
                  )}
                  <p className="text-sm text-gray-600">
                    Créé le {format(new Date(refinement.created_at), 'dd/MM/yyyy à HH:mm')}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedRefinement(refinement)}
                  className="text-blue-600 hover:text-blue-800"
                  title="Voir détails"
                >
                  <Eye size={20} />
                </button>
              </div>

              {refinement?.rationale && (
                <div className="mb-4">
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                    <strong>Justification:</strong> {refinement?.rationale}
                  </p>
                </div>
              )}

              {refinement?.impact_assessment && (
                <div className="mb-4">
                  <p className="text-sm text-gray-700 bg-blue-50 p-3 rounded-lg">
                    <strong>Impact:</strong> {refinement?.impact_assessment}
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center space-x-3 mt-4">
                {refinement?.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleApprove(refinement?.id)}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <CheckCircle size={16} />
                      <span>Approuver</span>
                    </button>
                    <button
                      onClick={() => handleReject(refinement?.id)}
                      className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <XCircle size={16} />
                      <span>Rejeter</span>
                    </button>
                  </>
                )}
                <button
                  onClick={() => onDelete(refinement?.id)}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  <Trash2 size={16} />
                  <span>Supprimer</span>
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      {/* Detail Modal */}
      {selectedRefinement && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Détails du Refinement</h3>
                <button onClick={() => setSelectedRefinement(null)} className="text-gray-400 hover:text-gray-600">
                  <XCircle size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Titre</label>
                  <p className="text-gray-900 font-semibold">{selectedRefinement?.title}</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <p className="text-gray-900">{getTypeLabel(selectedRefinement?.refinement_type)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priorité</label>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getPriorityColor(selectedRefinement?.priority)}`}>
                      {selectedRefinement?.priority}
                    </span>
                  </div>
                </div>

                {selectedRefinement?.current_content && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contenu Actuel</label>
                    <pre className="bg-gray-50 p-4 rounded-lg text-sm overflow-x-auto">
                      {JSON.stringify(selectedRefinement?.current_content, null, 2)}
                    </pre>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contenu Proposé</label>
                  <pre className="bg-green-50 p-4 rounded-lg text-sm overflow-x-auto border border-green-200">
                    {JSON.stringify(selectedRefinement?.proposed_content, null, 2)}
                  </pre>
                </div>

                {selectedRefinement?.rationale && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Justification</label>
                    <p className="text-gray-900 bg-gray-50 p-4 rounded-lg">{selectedRefinement?.rationale}</p>
                  </div>
                )}

                {selectedRefinement?.impact_assessment && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Analyse d'Impact</label>
                    <p className="text-gray-900 bg-blue-50 p-4 rounded-lg">{selectedRefinement?.impact_assessment}</p>
                  </div>
                )}

                {selectedRefinement?.review_notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes de Revue</label>
                    <p className="text-gray-900 bg-yellow-50 p-4 rounded-lg">{selectedRefinement?.review_notes}</p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setSelectedRefinement(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}